import React, { useState } from 'react';
import { ParsedQuestion } from '../../utils/surveyImport';
import { QuestionType } from '../../types/survey.types';

interface ImportPreviewProps {
  questions: ParsedQuestion[];
  onConfirm: (questions: ParsedQuestion[]) => void;
  onCancel: () => void;
}

const ImportPreview: React.FC<ImportPreviewProps> = ({ questions, onConfirm, onCancel }) => {
  const [editedQuestions, setEditedQuestions] = useState<ParsedQuestion[]>(questions);

  const questionTypeOptions: { value: QuestionType; label: string }[] = [
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text (Multi-line)' },
    { value: 'number', label: 'Number' },
    { value: 'radio', label: 'Single Choice (Radio)' },
    { value: 'checkbox', label: 'Multiple Choice (Checkbox)' },
    { value: 'dropdown', label: 'Dropdown Select' },
    { value: 'multiselect', label: 'Multi-Select Dropdown' },
    { value: 'scale', label: 'Scale (Likert)' },
    { value: 'rating', label: 'Star Rating' },
    { value: 'date', label: 'Date Picker' },
    { value: 'yesno', label: 'Yes/No' },
    { value: 'email', label: 'Email Address' },
    { value: 'phone', label: 'Phone Number' }
  ];

  const handleQuestionChange = (index: number, field: keyof ParsedQuestion, value: any) => {
    const updated = [...editedQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setEditedQuestions(updated);
  };

  const handleOptionsChange = (index: number, optionsText: string) => {
    const options = optionsText.split('|').map(o => o.trim()).filter(Boolean);
    handleQuestionChange(index, 'options', options);
  };

  const handleDeleteQuestion = (index: number) => {
    setEditedQuestions(editedQuestions.filter((_, i) => i !== index));
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= editedQuestions.length) return;

    const updated = [...editedQuestions];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setEditedQuestions(updated);
  };

  const getConfidenceColor = (confidence?: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high': return 'var(--color-success)';
      case 'medium': return 'var(--color-accent)';
      case 'low': return '#9CA3AF';
      default: return '#9CA3AF';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8 flex flex-col" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
        {/* Header - Fixed */}
        <div className="px-4 py-3 flex-shrink-0" style={{ borderBottom: '3px solid var(--color-accent)' }}>
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.5rem',
            color: 'var(--color-primary)',
            margin: 0
          }}>
            Review Imported Questions
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            {editedQuestions.length} question{editedQuestions.length !== 1 ? 's' : ''} detected. Review and adjust before importing.
          </p>
        </div>

        {/* Questions List - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: 0 }}>
          <div className="space-y-3">
            {editedQuestions.map((question, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow"
              >
                {/* Question Header */}
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveQuestion(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move up"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveQuestion(index, 'down')}
                      disabled={index === editedQuestions.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move down"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex-1 space-y-2">
                    {/* Question Number & Confidence */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span style={{
                        fontFamily: 'var(--font-heading)',
                        fontSize: '13px',
                        color: 'var(--color-primary)',
                        fontWeight: 600
                      }}>
                        Q{index + 1}
                      </span>
                      {question.confidence && (
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full"
                          style={{
                            backgroundColor: `${getConfidenceColor(question.confidence)}20`,
                            color: getConfidenceColor(question.confidence),
                            fontSize: '10px'
                          }}
                        >
                          {question.confidence}
                        </span>
                      )}
                      {question.detectedFrom && (
                        <span className="text-xs text-gray-500" style={{ fontSize: '10px' }}>
                          • {question.detectedFrom}
                        </span>
                      )}
                    </div>

                    {/* Question Text */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">Question</label>
                      <input
                        type="text"
                        value={question.text}
                        onChange={(e) => handleQuestionChange(index, 'text', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        style={{ borderColor: '#D1D5DB', fontSize: '13px' }}
                      />
                    </div>

                    {/* Question Type & Required */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">Type</label>
                        <select
                          value={question.type}
                          onChange={(e) => handleQuestionChange(index, 'type', e.target.value as QuestionType)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                          style={{ borderColor: '#D1D5DB', fontSize: '12px' }}
                        >
                          {questionTypeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-end">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={question.required}
                            onChange={(e) => handleQuestionChange(index, 'required', e.target.checked)}
                            className="mr-1.5"
                          />
                          <span className="text-xs text-gray-700">Required</span>
                        </label>
                      </div>
                    </div>

                    {/* Options (for radio, checkbox, dropdown) */}
                    {(question.type === 'radio' || question.type === 'checkbox' || question.type === 'dropdown') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          Options (use | to separate)
                        </label>
                        <input
                          type="text"
                          value={question.options?.join(' | ') || ''}
                          onChange={(e) => handleOptionsChange(index, e.target.value)}
                          placeholder="Option 1 | Option 2 | Option 3"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                          style={{ borderColor: '#D1D5DB', fontSize: '12px' }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDeleteQuestion(index)}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete question"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

            {editedQuestions.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>No questions to import. All questions were removed.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>

          <button
            onClick={() => onConfirm(editedQuestions)}
            disabled={editedQuestions.length === 0}
            className="px-6 py-2 rounded-lg font-medium transition-all duration-200"
            style={{
              backgroundColor: editedQuestions.length === 0 ? '#E5E7EB' : 'var(--color-accent)',
              color: editedQuestions.length === 0 ? '#9CA3AF' : 'white',
              cursor: editedQuestions.length === 0 ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (editedQuestions.length > 0) {
                e.currentTarget.style.backgroundColor = '#9C7D32';
              }
            }}
            onMouseLeave={(e) => {
              if (editedQuestions.length > 0) {
                e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              }
            }}
          >
            Import {editedQuestions.length} Question{editedQuestions.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportPreview;
